import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CdTDocument, CreatedDocument, Orientation } from '@app/features/documents/document.model';
import { RUNTIME_CONFIG } from '@environments/runtime-config.token';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  private httpClient = inject(HttpClient);

  private url = `${inject(RUNTIME_CONFIG).apiUrl}/contracts`;
  private previewDocumentSubject = new BehaviorSubject<CdTDocument | null>(null);

  get previewDocument$(): Observable<CdTDocument | null> {
    return this.previewDocumentSubject.asObservable();
  }

  fetchBlob(document: CdTDocument): Observable<Blob> {
    if (document.blob) {
      return of(document.blob);
    }
    return this.getDocumentBlob(document.id).pipe(
      tap((blob) => {
        document.blob = blob;
      }),
    );
  }

  /**
   * Ouvre la visionneuse en volet sur un document. Elle s'ouvre sans attendre le fichier :
   * c'est elle qui le charge, et qui dit qu'il charge ou pourquoi il ne s'affiche pas.
   */
  preview(document: CdTDocument) {
    this.previewDocumentSubject.next(document);
  }

  closePreview() {
    this.previewDocumentSubject.next(null);
  }

  /** Dépose un fichier ; renvoie le document créé, qui fait foi sur le fichier conservé. */
  createDocument(file: File): Observable<CreatedDocument> {
    const formData = new FormData();
    formData.append('file', file);

    return this.httpClient.post<CreatedDocument>(`${this.url}/documents`, formData);
  }

  deleteDocument(documentId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.url}/documents/${documentId}`);
  }

  /**
   * Retient l'orientation d'une page d'un document (RG-document-orientation-memorisee) ; le
   * fichier n'est pas modifié. Renvoie le document à jour.
   */
  setOrientation(documentId: string, page: number, degrees: Orientation): Observable<CdTDocument> {
    return this.httpClient.put<CdTDocument>(
      `${this.url}/documents/${documentId}/orientations/${page}`,
      { degrees },
    );
  }

  getDocumentBlob(documentId: string): Observable<Blob> {
    return this.httpClient.get(`${this.url}/documents/${documentId}/blob`, {
      responseType: 'blob',
    });
  }
}
